// englishGCSE.js
// AQA Y10/Y11 GCSE Literature modules.
// Sections:
// 8 — Macbeth (AQA Literature Paper 1 Section A)
// 9 — A Christmas Carol (AQA Literature Paper 1 Section B)
//
// Structure matches the Grade 9 Ready topic schema:
// - explainer
// - interactive
// - workedExample
// - practice
// - application
// - improvement

const tier = (label, text, critique) => ({ label, text, critique });

const makeInteractive = (title, items = []) =>
  items.length
    ? items
    : [
        {
          q: `Which skill is most important when answering an exam question on ${title}?`,
          type: 'mcq',
          options: [
            'Retelling the whole plot',
            'Making a focused argument using evidence',
            'Writing as many quotations as possible',
            'Only explaining historical context',
          ],
          answer: 1,
          feedback:
            'GCSE Literature rewards a clear argument supported by precise textual evidence, close analysis and relevant context.',
        },
        {
          q: `True or false: strong answers on ${title} should link the extract to the whole text.`,
          type: 'truefalse',
          answer: true,
          feedback:
            'For AQA Literature Paper 1, students are expected to write about the extract and the wider text.',
        },
        {
          q: `What does AO2 mainly assess in a ${title} answer?`,
          type: 'mcq',
          options: [
            'Historical facts only',
            'Spelling and punctuation only',
            'Analysis of language, form and structure',
            'Personal opinion without evidence',
          ],
          answer: 2,
          feedback:
            'AO2 is about how the writer uses language, form and structure to create meaning and effects.',
        },
      ];

const makePractice = (title, extra = []) => [
  `Write a thesis statement for an essay on ${title}.`,
  `Choose one short quotation linked to ${title} and explain its effect.`,
  `Write one PEEL paragraph using an embedded quotation.`,
  `Add one relevant AO3/context sentence without bolting it on.`,
  `Improve your paragraph by adding a precise technique word.`,
  ...extra,
];

const t = (id, slug, title, summary, c) => ({
  id,
  slug,
  title,
  summary,
  yearGroup: c.year || '10-11',
  duration: c.duration || '35-50 mins',
  xp: c.xp || 140,
  difficulty: c.difficulty || 'gcse',

  explainer: {
    title,
    intro: c.intro,
    keyPoints: c.keyPoints || [],
    rule: {
      name: c.ruleName || title,
      description: c.rule,
    },
    examples: c.examples || [],
    examFocus:
      c.examFocus ||
      'Focus on AO1 argument, AO2 analysis of methods, and AO3 context where relevant.',
    commonMistakes:
      c.commonMistakes || [
        'Retelling the plot instead of answering the question.',
        'Dropping quotations into the paragraph without analysing them.',
        'Adding context as a separate fact rather than linking it to the writer’s purpose.',
      ],
  },

  interactive: makeInteractive(title, c.interactive),

  workedExample:
    c.we || {
      prompt:
        c.wePrompt ||
        `How would you write a strong analytical paragraph on ${title}?`,
      tiers: [
        tier(
          'Developing',
          c.developing ||
            `The writer presents ${title} as important. This is shown through a quotation from the text. This makes the reader think about the theme.`,
          'This makes a relevant point, but it is too general. It needs a precise quotation, technique and explanation of effect.'
        ),
        tier(
          'Secure',
          c.secure ||
            `The writer presents ${title} through carefully chosen language and structure. The quotation suggests a clear idea, and this links to the wider message of the text.`,
          'This is clearer and more focused, but it could push further into individual words and writer’s purpose.'
        ),
        tier(
          'Excellent',
          c.modelAnswer ||
            `The writer presents ${title} as central to the text’s moral and dramatic purpose. Through precise language choices, the writer encourages the audience or reader to question character, power and responsibility.`,
          'This is conceptual and analytical. It links method, meaning and writer’s purpose rather than simply explaining what happens.'
        ),
      ],
    },

  practice: c.practice || makePractice(title, c.extraPractice),

  application: c.application || {
    prompt: c.appPrompt || `Write an analytical paragraph on ${title}.`,
    guidance:
      c.appGuidance ||
      'Use a clear thesis, embedded quotation, close word-level analysis, method terminology and relevant context.',
    wordTarget: c.wordTarget || '180-250',
    techniques: c.techniques || [
      'AO1 thesis',
      'Embedded quotation',
      'AO2 language analysis',
      'AO3 contextual link',
      'Writer’s purpose',
    ],
  },

  improvement: {
    lead:
      c.improve ||
      `Re-read your answer on ${title}. Have you answered the exact question, analysed a quotation closely, and linked your point to the writer’s purpose?`,
    checklist: c.checklist || [
      'Have I answered the exact wording of the question?',
      'Have I used a short, embedded quotation?',
      'Have I zoomed in on one or two key words?',
      'Have I named a method accurately?',
      'Have I linked to context only where it strengthens the argument?',
    ],
  },
});

// =====================================================================
// SECTION 8 — MACBETH
// AQA Literature Paper 1 Section A — Shakespeare
// =====================================================================

const macbethTopics = [
  t(
    'eng-8-1',
    'macbeth-plot-overview',
    'Macbeth — Plot Overview',
    'Five-act summary of Shakespeare’s tragedy.',
    {
      intro:
        'Macbeth is a Jacobean tragedy about a respected Scottish warrior whose ambition, encouraged by supernatural prophecy and Lady Macbeth’s persuasion, leads him to murder Duncan, seize the crown, rule as a tyrant and die violently.',
      keyPoints: [
        'Act 1: The witches prophesy Macbeth will become king; Lady Macbeth persuades him to kill Duncan.',
        'Act 2: Macbeth murders Duncan and immediately experiences guilt, fear and disorder.',
        'Act 3: Macbeth becomes king but grows paranoid; Banquo is murdered and Fleance escapes.',
        'Act 4: Macbeth trusts the witches’ apparitions and orders the murder of Macduff’s family.',
        'Act 5: Lady Macbeth collapses psychologically; Macbeth is defeated by Macduff and Malcolm restores order.',
      ],
      rule:
        'The tragic arc moves from honour to ambition, from ambition to regicide, from regicide to tyranny, and from tyranny to downfall.',
      examples: [
        {
          label: 'Tragic pattern',
          text:
            'Macbeth begins as “brave Macbeth” but ends as a “dead butcher”, showing how Shakespeare charts moral collapse.',
        },
        {
          label: 'Big idea',
          text:
            'Each act strips something from Macbeth: loyalty, peace, friendship, humanity and finally life.',
        },
      ],
      examFocus:
        'Plot knowledge should support analysis. Do not retell the story; use the structure of the tragedy to explain Shakespeare’s message.',
      modelAnswer:
        'Shakespeare structures Macbeth as a tragedy of moral collapse. Macbeth’s rise is deliberately rapid, moving from “brave Macbeth” to king in only two acts, but this speed makes his fall feel inevitable. The tragic structure warns that ambition without moral restraint destroys not only the individual but the nation itself.',
    }
  ),

  t(
    'eng-8-2',
    'macbeth-act-1',
    'Macbeth — Act 1: Prophecy and Persuasion',
    'The witches’ prophecy, Macbeth’s ambition and Lady Macbeth’s influence.',
    {
      intro:
        'Act 1 introduces the supernatural and shows Macbeth moving from loyal warrior to potential regicide. The witches awaken his ambition, but Lady Macbeth turns thought into action.',
      keyPoints: [
        'The witches greet Macbeth as Thane of Glamis, Thane of Cawdor and “king hereafter”.',
        'Macbeth’s aside reveals hidden ambition: “Stars, hide your fires”.',
        'Lady Macbeth fears Macbeth is “too full o’ the milk of human kindness”.',
        'Lady Macbeth calls on spirits to “unsex me here”, rejecting conventional femininity.',
        'Macbeth hesitates, but Lady Macbeth attacks his masculinity and resolve.',
      ],
      rule:
        'Act 1 is about temptation becoming decision: Macbeth does not simply hear a prophecy; he chooses to act on it.',
      examples: [
        {
          label: 'Key quote',
          text:
            '“Stars, hide your fires; / Let not light see my black and deep desires.”',
        },
        {
          label: 'Analysis note',
          text:
            'The contrast between light and darkness shows Macbeth already knows his desires are morally corrupt.',
        },
      ],
      modelAnswer:
        'In Act 1, Shakespeare presents Macbeth as a man already divided between loyalty and ambition. His plea for the “stars” to “hide” their “fires” suggests that his desire for power is shameful and must be concealed. The imagery of darkness implies moral corruption, while “deep desires” reveals that the witches have not created his ambition from nothing; they have exposed what already exists.',
    }
  ),

  t(
    'eng-8-3',
    'macbeth-act-2',
    'Macbeth — Act 2: The Murder of Duncan',
    'Regicide, guilt and immediate disorder.',
    {
      intro:
        'Act 2 is the irreversible turning point of the play. Macbeth murders Duncan offstage, but Shakespeare focuses on the psychological consequences rather than the violent act itself.',
      keyPoints: [
        'Macbeth hallucinates a dagger before the murder.',
        'Duncan is killed offstage, making the audience focus on guilt and imagination.',
        'Macbeth brings the daggers away from the scene, showing panic.',
        'Lady Macbeth claims “A little water clears us of this deed”.',
        'The natural and political order collapses after regicide.',
      ],
      rule:
        'Act 2 turns ambition into action. From this point onward, Macbeth cannot return to innocence.',
      examples: [
        {
          label: 'Key quote',
          text: '“Will all great Neptune’s ocean wash this blood / Clean from my hand?”',
        },
        {
          label: 'Motif',
          text:
            'Blood becomes a recurring symbol of guilt that cannot be washed away.',
        },
      ],
      modelAnswer:
        'Shakespeare presents Duncan’s murder as a spiritual and psychological catastrophe. Macbeth’s question, “Will all great Neptune’s ocean wash this blood / Clean from my hand?”, uses hyperbole to show guilt as immeasurable. The image of an entire ocean being unable to cleanse him suggests that regicide has stained not only his hands but his soul.',
    }
  ),

  t(
    'eng-8-4',
    'macbeth-act-3',
    'Macbeth — Act 3: Tyranny Begins',
    'Banquo’s murder, paranoia and the banquet scene.',
    {
      intro:
        'In Act 3, Macbeth shifts from reluctant murderer to active tyrant. He now kills not to gain power but to secure it.',
      keyPoints: [
        'Macbeth fears Banquo because of the prophecy about Banquo’s descendants.',
        'He hires murderers, showing he can now plan violence without Lady Macbeth.',
        'Banquo is killed but Fleance escapes.',
        'Banquo’s ghost appears at the banquet, exposing Macbeth’s private guilt in public.',
        'Lady Macbeth loses control over both Macbeth and the situation.',
      ],
      rule:
        'Act 3 shows ambition becoming paranoia: Macbeth’s crown gives him power, but not security.',
      examples: [
        {
          label: 'Key quote',
          text: '“To be thus is nothing, but to be safely thus.”',
        },
        {
          label: 'Key quote',
          text: '“O, full of scorpions is my mind, dear wife!”',
        },
      ],
      modelAnswer:
        'In Act 3, Shakespeare presents kingship as psychologically unstable when gained through violence. Macbeth’s claim that “to be thus is nothing, but to be safely thus” reveals that becoming king has not satisfied him. The repeated idea of safety shows his insecurity, while the balanced phrasing suggests that power without legitimacy is empty.',
    }
  ),

  t(
    'eng-8-5',
    'macbeth-act-4',
    'Macbeth — Act 4: The Tipping Point',
    'Apparitions, false confidence and the murder of Macduff’s family.',
    {
      intro:
        'Act 4 shows Macbeth at his most brutal. The witches’ apparitions make him overconfident, but his decision to murder Macduff’s family marks a new level of cruelty.',
      keyPoints: [
        'The apparitions warn Macbeth about Macduff but mislead him with equivocal language.',
        'Macbeth believes he is almost invincible.',
        'He orders the murder of Lady Macduff and her son.',
        'Macduff joins Malcolm in England.',
        'The act prepares the moral and military response to Macbeth’s tyranny.',
      ],
      rule:
        'Act 4 makes Macbeth almost irredeemable: violence is no longer strategic only; it becomes excessive and cruel.',
      examples: [
        {
          label: 'Key quote',
          text: '“From this moment / The very firstlings of my heart shall be / The firstlings of my hand.”',
        },
        {
          label: 'Key quote',
          text: '“Bleed, bleed, poor country!”',
        },
      ],
      modelAnswer:
        'Shakespeare presents Macbeth’s tyranny as impulsive and dehumanised in Act 4. His decision that the “firstlings” of his heart will become the “firstlings” of his hand shows that thought and violence have become almost simultaneous. The repetition suggests speed and recklessness, revealing a ruler who no longer allows conscience to interrupt action.',
    }
  ),

  t(
    'eng-8-6',
    'macbeth-act-5',
    'Macbeth — Act 5: Downfall',
    'Lady Macbeth’s madness, Birnam Wood and Macbeth’s death.',
    {
      intro:
        'Act 5 fulfils the witches’ prophecies while exposing their deception. Lady Macbeth collapses under guilt, Macbeth recognises life’s emptiness, and Macduff kills him.',
      keyPoints: [
        'Lady Macbeth sleepwalks and imagines blood on her hands.',
        'Macbeth responds to her death with nihilistic despair.',
        'Birnam Wood appears to move when soldiers carry branches as camouflage.',
        'Macduff reveals he was born by Caesarean section.',
        'Macbeth dies and Malcolm becomes king, restoring order.',
      ],
      rule:
        'The ending restores political and moral order, but only after Scotland has been damaged by tyranny.',
      examples: [
        {
          label: 'Key quote',
          text: '“Out, damned spot!”',
        },
        {
          label: 'Key quote',
          text: '“Life’s but a walking shadow.”',
        },
      ],
      modelAnswer:
        'In Act 5, Shakespeare presents guilt as inescapable. Lady Macbeth’s command “Out, damned spot!” reverses her earlier confidence that “a little water” would clear the deed. The adjective “damned” gives the blood spiritual significance, suggesting that her guilt is not merely emotional but connected to sin and eternal judgement.',
    }
  ),

  t(
    'eng-8-7',
    'macbeth-character-macbeth',
    'Macbeth — The Tragic Hero',
    'Macbeth’s journey from noble warrior to tyrant.',
    {
      intro:
        'Macbeth begins as a celebrated soldier but is destroyed by ambition, fear and moral weakness. His tragedy is powerful because he knows murder is wrong and chooses it anyway.',
      keyPoints: [
        'He is introduced as “brave Macbeth”, loyal and militarily heroic.',
        'His soliloquies reveal conscience and imagination.',
        'His hamartia is “vaulting ambition”.',
        'He becomes increasingly isolated and tyrannical.',
        'His death restores order but also completes his tragic fall.',
      ],
      rule:
        'Macbeth is tragic because he has agency: the witches tempt him, but they do not force him.',
      examples: [
        {
          label: 'Key quote',
          text: '“I have no spur / To prick the sides of my intent, but only / Vaulting ambition.”',
        },
      ],
      modelAnswer:
        'Shakespeare presents Macbeth as a tragic hero because he understands the moral consequences of his actions yet continues. His metaphor of “vaulting ambition” suggests a rider who jumps too high and falls. This image predicts his downfall and shows that Macbeth’s greatest danger is not Duncan, Banquo or Macduff, but his own excessive desire for power.',
    }
  ),

  t(
    'eng-8-8',
    'macbeth-character-lady-macbeth',
    'Lady Macbeth',
    'The catalyst who appears powerful but collapses psychologically.',
    {
      intro:
        'Lady Macbeth is initially presented as ruthless, persuasive and dominant. However, Shakespeare later reveals the psychological cost of suppressing guilt and rejecting moral feeling.',
      keyPoints: [
        'She reads Macbeth’s letter and immediately plans Duncan’s murder.',
        'She asks spirits to “unsex” her and fill her with cruelty.',
        'She manipulates Macbeth by questioning his masculinity.',
        'She appears calm after the murder but later sleepwalks.',
        'Her collapse contrasts with her earlier confidence.',
      ],
      rule:
        'Track Lady Macbeth as a reversal: apparent strength in Act 1 becomes psychological fragility by Act 5.',
      examples: [
        {
          label: 'Key quote',
          text: '“Look like the innocent flower, / But be the serpent under’t.”',
        },
        {
          label: 'Key quote',
          text: '“Out, damned spot!”',
        },
      ],
      modelAnswer:
        'Shakespeare presents Lady Macbeth as a character who tries to separate appearance from reality. Her command to “look like the innocent flower” but be the “serpent” beneath it uses biblical imagery to associate deception with evil. The contrast between “flower” and “serpent” also reveals her understanding that power depends on performance and concealment.',
    }
  ),

  t(
    'eng-8-9',
    'macbeth-character-witches',
    'The Witches & The Supernatural',
    'Prophecy, temptation, equivocation and moral disorder.',
    {
      intro:
        'The witches open the play and establish a world of disorder, ambiguity and temptation. Their prophecies are true but misleading, encouraging Macbeth to destroy himself.',
      keyPoints: [
        'They appear in thunder and lightning, linking them to chaos.',
        '“Fair is foul” introduces moral inversion.',
        'Their language is riddling and equivocal.',
        'They predict Macbeth’s rise but never directly command murder.',
        'Jacobean audiences would connect witchcraft with evil and disorder.',
      ],
      rule:
        'The witches are catalysts, not excuses: Macbeth chooses how to respond.',
      examples: [
        {
          label: 'Key quote',
          text: '“Fair is foul, and foul is fair.”',
        },
        {
          label: 'Technique',
          text:
            'Their chant-like trochaic rhythm separates them from the noble characters’ verse.',
        },
      ],
      modelAnswer:
        'Shakespeare presents the witches as agents of moral confusion. Their paradox “fair is foul, and foul is fair” collapses the boundary between good and evil before Macbeth even appears. This prepares the audience for a world in which appearances deceive and ambition can disguise itself as destiny.',
    }
  ),

  t(
    'eng-8-10',
    'macbeth-theme-ambition',
    'Theme: Ambition',
    'Desire for power that overreaches and destroys.',
    {
      intro:
        'Ambition drives the tragedy. Shakespeare does not condemn all ambition; he condemns ambition that breaks moral, political and divine order.',
      keyPoints: [
        'Macbeth’s ambition is excessive and self-destructive.',
        'Lady Macbeth’s ambition is initially stronger and more focused.',
        'Banquo hears the same prophecy but does not act immorally.',
        'Ambition leads to regicide, tyranny and isolation.',
        'Jacobean audiences would view unlawful ambition for the crown as a sin against divine order.',
      ],
      rule:
        'The strongest essays contrast Macbeth’s ambition with Banquo’s restraint.',
      examples: [
        {
          label: 'Key quote',
          text: '“Vaulting ambition, which o’erleaps itself.”',
        },
      ],
      modelAnswer:
        'Shakespeare presents ambition as dangerous when it is not controlled by conscience. Macbeth’s phrase “vaulting ambition” suggests movement, energy and aspiration, but “o’erleaps itself” immediately implies failure. The metaphor therefore contains its own warning: ambition that exceeds moral limits becomes self-defeating.',
    }
  ),

  t(
    'eng-8-11',
    'macbeth-theme-guilt',
    'Theme: Guilt & Conscience',
    'Blood, sleep, hallucination and psychological punishment.',
    {
      intro:
        'Guilt in Macbeth is not abstract; Shakespeare makes it visible through blood, sleeplessness, hallucinations and disturbed behaviour.',
      keyPoints: [
        'Macbeth hallucinates before and after murder.',
        'Blood symbolises guilt and moral stain.',
        'Sleep represents innocence and peace.',
        'Banquo’s ghost externalises Macbeth’s conscience.',
        'Lady Macbeth’s sleepwalking shows guilt returning unconsciously.',
      ],
      rule:
        'Track motifs: blood, sleep and hallucination are Shakespeare’s main methods for presenting guilt.',
      examples: [
        {
          label: 'Key quote',
          text: '“Macbeth shall sleep no more.”',
        },
        {
          label: 'Key quote',
          text: '“Will all great Neptune’s ocean wash this blood clean from my hand?”',
        },
      ],
      modelAnswer:
        'Shakespeare presents guilt as a punishment that begins immediately after the crime. Macbeth’s belief that he has “murdered sleep” turns sleep into a symbol of innocence and peace. Because sleep is natural and restorative, losing it suggests that Macbeth has severed himself from the natural and moral order.',
    }
  ),

  t(
    'eng-8-12',
    'macbeth-theme-fate',
    'Theme: Fate vs Free Will',
    'Prophecy, temptation and personal choice.',
    {
      intro:
        'Macbeth explores whether people are controlled by fate or responsible for their choices. Shakespeare keeps the question deliberately ambiguous.',
      keyPoints: [
        'The witches predict Macbeth’s future but do not tell him to kill Duncan.',
        'Macbeth initially imagines chance may crown him “without my stir”.',
        'Banquo acts as a contrast because he resists acting on prophecy.',
        'Macbeth repeatedly chooses violence.',
        'The apparitions make Macbeth overconfident but not innocent.',
      ],
      rule:
        'High-level responses argue both sides: supernatural influence matters, but Macbeth remains morally responsible.',
      examples: [
        {
          label: 'Key quote',
          text: '“If chance will have me king, why, chance may crown me, / Without my stir.”',
        },
      ],
      modelAnswer:
        'Shakespeare presents fate as tempting but not controlling. Macbeth’s hope that “chance may crown” him “without my stir” shows he initially understands the possibility of passive destiny. However, the phrase “my stir” also reveals that action is a choice available to him, making his later violence morally significant.',
    }
  ),

  t(
    'eng-8-13',
    'macbeth-theme-appearance',
    'Theme: Appearance vs Reality',
    'Deception, equivocation and hidden corruption.',
    {
      intro:
        'Macbeth repeatedly shows that appearances cannot be trusted. Characters hide ambition, prophecies mislead and public order masks private terror.',
      keyPoints: [
        'The witches’ “fair is foul” establishes moral inversion.',
        'Lady Macbeth advises Macbeth to disguise his intentions.',
        'Duncan misjudges Macbeth as loyal.',
        'Macbeth appears kingly but rules illegitimately.',
        'The apparitions seem reassuring but conceal danger.',
      ],
      rule:
        'Appearance vs reality links strongly to equivocation, deception and Jacobean fears after the Gunpowder Plot.',
      examples: [
        {
          label: 'Key quote',
          text: '“Look like the innocent flower, / But be the serpent under’t.”',
        },
      ],
      modelAnswer:
        'Shakespeare presents deception as central to Macbeth’s rise. Lady Macbeth’s image of the “innocent flower” hiding the “serpent” uses natural beauty to mask biblical evil. The contrast suggests that Macbeth’s success depends on his ability to perform loyalty while concealing treason.',
    }
  ),

  t(
    'eng-8-14',
    'macbeth-context',
    'Macbeth — Context (AO3)',
    'Jacobean kingship, witchcraft, treason and divine order.',
    {
      intro:
        'Macbeth was written during the reign of James I. The play reflects Jacobean concerns about witchcraft, treason, kingship, divine order and political stability.',
      keyPoints: [
        'James I was interested in witchcraft and wrote Daemonologie.',
        'The Divine Right of Kings made regicide a religious as well as political crime.',
        'The Gunpowder Plot of 1605 made treason and equivocation urgent concerns.',
        'Banquo is presented nobly partly because James I claimed descent from him.',
        'The restoration of Malcolm supports the idea that legitimate monarchy restores order.',
      ],
      rule:
        'Context should be woven into analysis of Shakespeare’s choices, not added as a separate history fact.',
      examples: [
        {
          label: 'AO3 sentence',
          text:
            'A Jacobean audience, familiar with the Divine Right of Kings, would have seen Duncan’s murder as an attack on God’s order.',
        },
      ],
      modelAnswer:
        'Shakespeare’s presentation of Duncan’s murder would have been especially shocking to a Jacobean audience because of the Divine Right of Kings. By showing Scotland descend into unnatural darkness, fear and tyranny after regicide, Shakespeare reinforces the belief that killing a rightful king damages the whole moral and political universe.',
    }
  ),

  t(
    'eng-8-15',
    'macbeth-key-quotations',
    'Macbeth — Key Quotations',
    'Essential quotations for themes, characters and exam answers.',
    {
      intro:
        'Strong GCSE answers use short, flexible quotations. The best quotations can be applied to more than one theme or character.',
      keyPoints: [
        'Ambition: “Vaulting ambition, which o’erleaps itself.”',
        'Guilt: “Will all great Neptune’s ocean wash this blood clean from my hand?”',
        'Appearance: “Look like the innocent flower, but be the serpent under’t.”',
        'Supernatural: “Fair is foul, and foul is fair.”',
        'Downfall: “Life’s but a walking shadow.”',
      ],
      rule:
        'Memorise short phrases, not huge chunks. Embed them smoothly into your own sentences.',
      examples: [
        {
          label: 'Embedding',
          text:
            'Macbeth’s “vaulting ambition” suggests that his desire for power is energetic but dangerously excessive.',
        },
      ],
      modelAnswer:
        'The phrase “vaulting ambition” is useful because it works for character, theme and structure. It reveals Macbeth’s self-awareness, links to the wider theme of ambition and foreshadows the tragic fall contained in “o’erleaps itself”.',
    }
  ),

  t(
    'eng-8-16',
    'macbeth-exam-technique',
    'Macbeth — Exam Technique',
    'How to answer AQA Literature Paper 1 Section A.',
    {
      intro:
        'In AQA Literature Paper 1 Section A, students answer one Macbeth question. They are given an extract and must write about the extract and the play as a whole.',
      keyPoints: [
        'Spend about 50 minutes on the Macbeth question.',
        'Read the question first, then annotate the extract for the focus word.',
        'Write about the extract first before moving to the wider play.',
        'AO1: argument and references.',
        'AO2: language, form and structure.',
        'AO3: relevant context.',
        'AO4: spelling, punctuation and grammar are assessed separately on Paper 1.',
      ],
      rule:
        'Extract first, whole play second. Do not ignore the printed extract.',
      examples: [
        {
          label: 'Paragraph opener',
          text:
            'In this extract, Shakespeare presents Macbeth as increasingly isolated through...',
        },
      ],
      application: {
        prompt:
          'Plan a response to: “Starting with this extract, explore how Shakespeare presents Macbeth as a violent character.”',
        guidance:
          'Use one extract paragraph, one whole-play paragraph from earlier in the play and one whole-play paragraph from later in the play.',
        wordTarget: '350-500',
        techniques: [
          'Extract reference',
          'Whole-play link',
          'Embedded quotation',
          'AO2 method analysis',
          'AO3 context',
        ],
      },
      modelAnswer:
        'A strong Macbeth answer begins with the question’s focus and builds an argument. Instead of writing everything known about Macbeth, it selects evidence from the extract and the wider play to show how Shakespeare develops one idea across the tragedy.',
    }
  ),
];

// =====================================================================
// SECTION 9 — A CHRISTMAS CAROL
// AQA Literature Paper 1 Section B — 19th-century novel
// =====================================================================

const accTopics = [
  t(
    'eng-9-1',
    'acc-plot-overview',
    'A Christmas Carol — Plot Overview',
    'Dickens’s 1843 novella in five staves.',
    {
      intro:
        'A Christmas Carol follows Ebenezer Scrooge’s moral transformation after he is visited by Marley’s ghost and the three Spirits of Christmas. Dickens uses the story to argue for compassion, generosity and social responsibility.',
      keyPoints: [
        'Stave 1: Scrooge is introduced as miserly and isolated; Marley warns him.',
        'Stave 2: The Ghost of Christmas Past shows Scrooge loneliness, Fezziwig and Belle.',
        'Stave 3: The Ghost of Christmas Present shows the Cratchits, Fred and Ignorance and Want.',
        'Stave 4: The Ghost of Christmas Yet to Come shows death, loss and Scrooge’s grave.',
        'Stave 5: Scrooge changes his behaviour and becomes generous and connected.',
      ],
      rule:
        'The novella is a redemption narrative: seeing the past, present and future forces Scrooge to change.',
      examples: [
        {
          label: 'Structure',
          text:
            'The five staves work like a moral journey: warning, memory, social responsibility, fear and transformation.',
        },
      ],
      modelAnswer:
        'Dickens structures A Christmas Carol as a journey from isolation to community. Each spirit forces Scrooge to see a different moral truth, so his transformation in Stave 5 feels earned rather than accidental. The novella’s structure therefore supports Dickens’s message that people can change if they truly confront the consequences of selfishness.',
    }
  ),

  t(
    'eng-9-2',
    'acc-stave-1',
    'Stave 1: Marley’s Ghost',
    'Scrooge’s coldness and Marley’s warning.',
    {
      intro:
        'Stave 1 establishes Scrooge as cold, miserly and socially isolated. Marley’s ghost then warns him that a life without compassion leads to spiritual imprisonment.',
      keyPoints: [
        'Scrooge is described with harsh verbs: “squeezing, wrenching, grasping”.',
        'He rejects charity and repeats the language of prisons and workhouses.',
        'Bob Cratchit works in poor conditions.',
        'Marley’s chain symbolises greed and selfish choices.',
        'The promise of three spirits creates the structure of the novella.',
      ],
      rule:
        'Dickens makes Scrooge physically and morally cold before offering him the chance to change.',
      examples: [
        {
          label: 'Key quote',
          text: '“Mankind was my business.”',
        },
      ],
      modelAnswer:
        'Dickens presents Marley as a warning version of Scrooge. His chain, “forged in life”, symbolises the consequences of selfish choices repeated over time. The verb “forged” suggests that Marley created his own punishment, reinforcing Dickens’s message that moral responsibility cannot be avoided.',
    }
  ),

  t(
    'eng-9-3',
    'acc-stave-2',
    'Stave 2: The Ghost of Christmas Past',
    'Memory, loneliness and the roots of Scrooge’s character.',
    {
      intro:
        'The Ghost of Christmas Past shows Scrooge the emotional experiences that helped shape him: childhood loneliness, Fan’s kindness, Fezziwig’s generosity and Belle’s rejection.',
      keyPoints: [
        'Young Scrooge is isolated at school, which humanises him.',
        'Fan represents family love and emotional warmth.',
        'Fezziwig is a model of generous employment.',
        'Belle leaves Scrooge because money has replaced love.',
        'Light imagery suggests memory, truth and moral exposure.',
      ],
      rule:
        'Stave 2 does not excuse Scrooge, but it helps the reader understand how he became emotionally closed.',
      examples: [
        {
          label: 'Key quote',
          text: '“Another idol has displaced me... a golden one.”',
        },
      ],
      modelAnswer:
        'Dickens uses Belle to show that Scrooge’s obsession with money has spiritual and emotional consequences. Her phrase “a golden one” turns wealth into an “idol”, suggesting false worship. This implies that Scrooge has not simply become rich; he has replaced human love with material value.',
    }
  ),

  t(
    'eng-9-4',
    'acc-stave-3',
    'Stave 3: The Ghost of Christmas Present',
    'The Cratchits, Tiny Tim, Fred, Ignorance and Want.',
    {
      intro:
        'Stave 3 forces Scrooge to look at the world around him. Dickens contrasts the Cratchits’ poverty with their warmth and uses Ignorance and Want to expose Victorian society’s neglect of the poor.',
      keyPoints: [
        'The Cratchits are poor but loving and dignified.',
        'Tiny Tim represents innocence and vulnerability.',
        'Fred’s Christmas party shows the warmth Scrooge rejects.',
        'Ignorance and Want are allegorical children.',
        'The Spirit repeats Scrooge’s own cruel words back to him.',
      ],
      rule:
        'Stave 3 is Dickens’s clearest social-responsibility section.',
      examples: [
        {
          label: 'Key quote',
          text: '“Beware them both, and all of their degree, but most of all beware this boy.”',
        },
      ],
      modelAnswer:
        'Dickens presents Ignorance and Want as a direct warning to Victorian society. By making abstract social problems appear as suffering children, he forces the reader to respond emotionally as well as intellectually. The command “beware” suggests that neglecting poverty will have dangerous consequences for everyone, not only the poor.',
    }
  ),

  t(
    'eng-9-5',
    'acc-stave-4',
    'Stave 4: The Ghost of Christmas Yet to Come',
    'Fear, death and the future Scrooge can still avoid.',
    {
      intro:
        'The final spirit shows Scrooge a lonely death, stolen possessions and Tiny Tim’s absence. Its silence makes Scrooge confront the truth himself.',
      keyPoints: [
        'The spirit is silent and frightening.',
        'Scrooge witnesses people reacting coldly to an unnamed man’s death.',
        'The thieves sell the dead man’s possessions.',
        'The Cratchits mourn Tiny Tim.',
        'Scrooge sees his own grave and begs for the chance to change.',
      ],
      rule:
        'The future is presented as conditional: terrifying, but avoidable if Scrooge changes.',
      examples: [
        {
          label: 'Key quote',
          text: '“I will honour Christmas in my heart, and try to keep it all the year.”',
        },
      ],
      modelAnswer:
        'Dickens presents the future as a moral warning rather than a fixed punishment. Scrooge’s promise to “honour Christmas” all year shows that he finally understands Christmas as a symbol of ongoing compassion, not a single day of celebration. His change must become permanent behaviour.',
    }
  ),

  t(
    'eng-9-6',
    'acc-stave-5',
    'Stave 5: The End of It',
    'Redemption shown through action.',
    {
      intro:
        'In Stave 5, Scrooge wakes on Christmas morning transformed. Dickens proves his redemption through behaviour: giving money, visiting Fred, helping Bob and becoming part of the community.',
      keyPoints: [
        'Scrooge wakes joyful and grateful.',
        'He sends a prize turkey to the Cratchits.',
        'He donates generously to charity.',
        'He joins Fred’s family celebration.',
        'He raises Bob’s salary and becomes a second father to Tiny Tim.',
      ],
      rule:
        'Redemption must be visible through action, not just emotion.',
      examples: [
        {
          label: 'Key quote',
          text: '“I am as light as a feather, I am as happy as an angel.”',
        },
      ],
      modelAnswer:
        'Dickens presents Scrooge’s redemption through energetic and joyful language. His claim that he is “as light as a feather” suggests that generosity has released him from the emotional weight of greed. The simile contrasts sharply with Marley’s heavy chain, showing that Scrooge has avoided the same spiritual fate.',
    }
  ),

  t(
    'eng-9-7',
    'acc-character-scrooge',
    'Scrooge — Character Arc',
    'From isolated miser to generous member of the community.',
    {
      intro:
        'Scrooge is the central character of the novella. Dickens presents him first as cruel and isolated, then gradually reveals his humanity, fear, regret and capacity to change.',
      keyPoints: [
        'At the start, Scrooge is “solitary as an oyster”.',
        'His childhood creates sympathy but not excuse.',
        'His emotional responses increase across the staves.',
        'He learns from the spirits by watching consequences.',
        'His final transformation is shown through generosity and community.',
      ],
      rule:
        'Track change across the whole novella. Scrooge is not static; he is deliberately transformed.',
      examples: [
        {
          label: 'Key contrast',
          text: '“Bah! Humbug!” becomes “A merry Christmas to everybody!”',
        },
      ],
      modelAnswer:
        'Dickens presents Scrooge as capable of change despite his extreme coldness. The description “solitary as an oyster” suggests he is closed, hard and difficult to reach, but it also hints that something valuable may exist inside. This simile prepares the reader for his later transformation rather than making him purely villainous.',
    }
  ),

  t(
    'eng-9-8',
    'acc-character-cratchits',
    'The Cratchits & Tiny Tim',
    'Poverty, dignity, family and vulnerability.',
    {
      intro:
        'The Cratchits represent the working poor. Dickens uses them to challenge the idea that poverty is caused by moral failure and to show that wealth is not the same as goodness.',
      keyPoints: [
        'Bob Cratchit works hard despite poor pay and conditions.',
        'Mrs Cratchit is loving but realistic about Scrooge’s cruelty.',
        'Tiny Tim represents innocence and social vulnerability.',
        'The family’s Christmas is modest but emotionally rich.',
        'Their suffering exposes the consequences of social inequality.',
      ],
      rule:
        'Use the Cratchits to discuss poverty, family, social responsibility and Dickens’s purpose.',
      examples: [
        {
          label: 'Key quote',
          text: '“God bless us, every one!”',
        },
      ],
      modelAnswer:
        'Dickens uses Tiny Tim to make poverty personal and emotionally urgent. His blessing, “God bless us, every one,” presents him as innocent, generous and spiritually pure. The inclusive phrase “every one” contrasts with Scrooge’s earlier selfishness and reinforces Dickens’s Christian message of compassion.',
    }
  ),

  t(
    'eng-9-9',
    'acc-character-spirits',
    'The Four Spirits',
    'Marley and the three Christmas ghosts as moral teachers.',
    {
      intro:
        'The spirits are structural and moral devices. Marley warns, Past explains, Present challenges and Yet to Come terrifies.',
      keyPoints: [
        'Marley shows the consequences of a selfish life.',
        'Past reveals how Scrooge became emotionally damaged.',
        'Present shows current suffering and joy outside Scrooge’s world.',
        'Yet to Come shows the consequences if Scrooge refuses change.',
        'The spirits guide Scrooge towards self-knowledge.',
      ],
      rule:
        'The spirits are supernatural, but their purpose is moral education.',
      examples: [
        {
          label: 'Structure',
          text:
            'Each spirit teaches a different lesson: regret, responsibility, fear and action.',
        },
      ],
      modelAnswer:
        'Dickens uses the spirits to externalise Scrooge’s conscience. Although they appear supernatural, each one forces him to confront truths he has ignored. This makes the novella both a ghost story and a moral education, using fear and wonder to produce ethical change.',
    }
  ),

  t(
    'eng-9-10',
    'acc-theme-redemption',
    'Theme: Redemption',
    'The possibility of moral change.',
    {
      intro:
        'Redemption is the central hopeful message of A Christmas Carol. Dickens argues that even deeply selfish people can change if they confront truth and act differently.',
      keyPoints: [
        'Marley cannot change because his life is over.',
        'Scrooge is given a chance while he is still alive.',
        'The spirits make him see consequences.',
        'His change is emotional, moral and practical.',
        'Stave 5 proves redemption through action.',
      ],
      rule:
        'Redemption in the novella is not a feeling; it is a changed way of living.',
      examples: [
        {
          label: 'Key quote',
          text: '“I will live in the Past, the Present, and the Future.”',
        },
      ],
      modelAnswer:
        'Dickens presents redemption as a process of moral awakening. Scrooge’s promise to “live in the Past, the Present, and the Future” shows that he has learned from memory, responsibility and consequence. The three-part structure mirrors the spirits’ lessons and suggests that genuine change requires a complete transformation of perspective.',
    }
  ),

  t(
    'eng-9-11',
    'acc-theme-poverty',
    'Theme: Poverty & Social Responsibility',
    'Dickens’s criticism of Victorian attitudes to the poor.',
    {
      intro:
        'A Christmas Carol attacks the belief that the poor are responsible for their own suffering. Dickens argues that society, especially the wealthy, has a moral duty to respond to poverty with compassion.',
      keyPoints: [
        'Scrooge initially rejects charity.',
        'He uses the language of prisons and workhouses.',
        'The Cratchits show poverty with dignity.',
        'Ignorance and Want make social problems visible.',
        'Dickens criticises systems that dehumanise the poor.',
      ],
      rule:
        'When writing about poverty, connect character, context and Dickens’s moral purpose.',
      examples: [
        {
          label: 'Key quote',
          text: '“Are there no prisons? Are there no workhouses?”',
        },
      ],
      modelAnswer:
        'Dickens criticises harsh Victorian attitudes to poverty through Scrooge’s question, “Are there no prisons? Are there no workhouses?” The rhetorical questions show Scrooge reducing human suffering to institutions. By later repeating these words through the Spirit, Dickens forces both Scrooge and the reader to recognise their cruelty.',
    }
  ),

  t(
    'eng-9-12',
    'acc-theme-family',
    'Theme: Family & Christmas',
    'Community, warmth and belonging.',
    {
      intro:
        'Family and Christmas represent connection in the novella. Dickens contrasts Scrooge’s isolation with the warmth of Fred’s home and the Cratchits’ family life.',
      keyPoints: [
        'Fred repeatedly invites Scrooge despite rejection.',
        'The Cratchits are poor but emotionally rich.',
        'Fan’s love shows what Scrooge lost.',
        'Belle’s family contrasts with Scrooge’s isolation.',
        'Scrooge joins the community at the end.',
      ],
      rule:
        'Christmas is not just a setting. It symbolises generosity, memory, forgiveness and social connection.',
      examples: [
        {
          label: 'Contrast',
          text:
            'Scrooge begins alone in his counting-house and ends welcomed into Fred’s family celebration.',
        },
      ],
      modelAnswer:
        'Dickens presents family as a source of moral warmth. Fred’s repeated invitation to Scrooge shows generosity without reward, directly contrasting with Scrooge’s transactional view of life. Through Fred and the Cratchits, Dickens suggests that emotional wealth matters more than financial wealth.',
    }
  ),

  t(
    'eng-9-13',
    'acc-context-and-exam',
    'A Christmas Carol — Context & Exam Technique',
    'Victorian context and AQA Paper 1 Section B strategy.',
    {
      intro:
        'AQA Literature Paper 1 Section B asks students to answer one question on the 19th-century novel. For A Christmas Carol, students should use the extract and the whole novella, linking Dickens’s methods to Victorian context where relevant.',
      keyPoints: [
        'Spend about 50 minutes on the question.',
        'Start with the extract, then move across the novella.',
        'AO1: focused argument and references.',
        'AO2: language, form and structure.',
        'AO3: Victorian poverty, Poor Law, workhouses, Christianity, industrialisation and social responsibility.',
        'Avoid context dumping: link context to Dickens’s message.',
      ],
      rule:
        'Use context to explain Dickens’s purpose, not to write a history paragraph.',
      examples: [
        {
          label: 'Exam sentence',
          text:
            'Dickens uses Scrooge’s language about workhouses to criticise Victorian systems that treated poverty as a moral failure.',
        },
      ],
      application: {
        prompt:
          'Plan a response to: “Starting with this extract, explore how Dickens presents ideas about social responsibility.”',
        guidance:
          'Use one extract paragraph, then two whole-novella paragraphs. Include Dickens’s purpose and Victorian context.',
        wordTarget: '350-500',
        techniques: [
          'Extract reference',
          'Whole-text link',
          'AO2 language analysis',
          'AO3 Victorian context',
          'Writer’s purpose',
        ],
      },
      modelAnswer:
        'A strong A Christmas Carol answer uses context selectively. Instead of writing everything known about the Poor Law, it links Victorian poverty directly to Dickens’s methods, such as his use of the Cratchits, Ignorance and Want, and Scrooge’s repeated references to prisons and workhouses.',
    }
  ),
];

export const englishGCSESections = [
  {
    id: 8,
    title: 'Macbeth (AQA GCSE)',
    slug: 'macbeth',
    color: 'red',
    icon: 'Theatre',
    description:
      'AQA Literature Paper 1 Section A — Shakespeare. Plot, characters, themes, context, quotations and exam technique.',
    examBoard: 'AQA',
    paper: 'Literature Paper 1',
    component: 'Section A — Shakespeare',
    topics: macbethTopics,
  },
  {
    id: 9,
    title: 'A Christmas Carol (AQA GCSE)',
    slug: 'a-christmas-carol',
    color: 'green',
    icon: 'BookOpen',
    description:
      'AQA Literature Paper 1 Section B — 19th-century novel. Five staves, character arcs, themes, Victorian context and exam technique.',
    examBoard: 'AQA',
    paper: 'Literature Paper 1',
    component: 'Section B — 19th-century novel',
    topics: accTopics,
  },
];